import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import BadgeService from "service/BadgeService";

export const getBadge = createAsyncThunk("getBadge", async (shopDomain) => {
  const response = await BadgeService.getAllBadge(shopDomain);
  return response.data;
});

export const updateBadge = createAsyncThunk("updateBadge", async ({ id, data }) => {
  const response = await BadgeService.updateBadge(id, data);
  return response.data;
});

// `condition.section` lists the enabled section rules: product, collection, include_section,
// ignore_section. Empty means no section filter at all. The two selector fields are plain
// comma-separated CSS selector strings, not lists.
const normalizeSection = (condition) => ({
  ...condition,
  section: Array.isArray(condition?.section) ? condition.section : [],
  include_sections: condition?.include_sections ?? "",
  ignore_sections: condition?.ignore_sections ?? "",
});

const checkSizeSetting = (state, object) => {
  if (
    state[`${object}`][`${object}_width_product`] ||
    state[`${object}`][`${object}_height_product`] ||
    state[`${object}`][`${object}_width_mobile`] ||
    state[`${object}`][`${object}_height_mobile`] ||
    state[`${object}`][`${object}_width_product_mobile`] ||
    state[`${object}`][`${object}_height_product_mobile`] ||
    state.design.font_size_product ||
    (state.design.font_size_mobile && state.design.font_size_mobile !== state.design.font_size) ||
    state.design.font_size_product_mobile
  ) {
    state.sizeMode = "custom";
  } else {
    state.sizeMode = "same";
  }
};

export const badgeSlice = createSlice({
  name: "badges",
  initialState: {
    badge: {},
    design: {},
    list: [],
    sizeMode: "same",
    openEditModal: false,
  },
  reducers: {
    setEditData: (state, action) => {
      const { design, ...badgeData } = action.payload;
      Object.assign(state.badge, badgeData);
      if (design) Object.assign(state.design, design);
      checkSizeSetting(state, "badge");
    },
    setBadgeAttribute: (state, action) => {
      Object.assign(state.badge, action.payload);
    },
    setConditionAttribute: (state, action) => {
      state.badge.conditions = { ...state.badge.conditions, ...action.payload };
    },
    setDesignAttribute: (state, action) => {
      Object.assign(state.design, action.payload);
    },
    setSizeMode: (state, action) => {
      state.sizeMode = action.payload;
    },
    toggleOpenEditModal: (state) => {
      state.openEditModal = !state.openEditModal;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getBadge.fulfilled, (state, action) => {
      state.list = action.payload.data;
    });
    builder.addCase(updateBadge.fulfilled, (state, action) => {
      const updated = action.payload?.data ?? action.payload;
      if (!updated?.id) return;
      const index = state.list.findIndex((badge) => badge.id === updated.id);
      if (index !== -1) {
        state.list[index] = updated;
      }
      const { design, ...badgeData } = updated;
      // Replace instead of merge: the response is the source of truth, so a field the
      // API dropped or changed must not keep the value we optimistically sent.
      state.badge = {
        ...badgeData,
        // No section data in the response means "All sections".
        conditions: normalizeSection(badgeData.conditions),
      };
      if (design) state.design = design;
      checkSizeSetting(state, "badge");
    });
  },
});

export const {
  setEditData,
  setBadgeAttribute,
  setConditionAttribute,
  setDesignAttribute,
  setSizeMode,
  toggleOpenEditModal,
} = badgeSlice.actions;

export default badgeSlice.reducer;
